import { api, tokenStore } from './client';

// ============= TYPES =============
export interface User {
  id: string;
  email: string;
  full_name: string | null;
  locale: string;
  timezone: string;
  created_at: string;
}

export interface AuthTokens { accessToken: string; refreshToken: string }

export interface EmailAccount {
  id: string;
  email_address: string;
  display_name: string | null;
  imap_host: string;
  smtp_host: string;
  sync_status: 'idle' | 'syncing' | 'error' | 'paused';
  sync_error: string | null;
  last_incremental_sync_at: string | null;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  // condivisione/delega
  shared?: boolean;
  shared_by?: string | null;
  can_send?: boolean;
}

export interface AccountShare {
  id: string;
  can_send: boolean;
  created_at: string;
  delegate_email: string;
  delegate_name: string | null;
}
export const sharesApi = {
  list: (accountId: string) => api.get<{ shares: AccountShare[] }>(`/accounts/${accountId}/shares`),
  create: (accountId: string, email: string, can_send: boolean) =>
    api.post<{ share: AccountShare }>(`/accounts/${accountId}/shares`, { email, can_send }),
  remove: (accountId: string, shareId: string) =>
    api.delete<{ ok: true }>(`/accounts/${accountId}/shares/${shareId}`),
};

export interface DiscoverConfig {
  imap: { host: string; port: number; secure: boolean };
  smtp: { host: string; port: number; secure: boolean };
  source: 'hardcoded' | 'convention' | 'srv' | 'mx' | 'autoconfig';
  /** Il gestore riconosciuto ('Aruba', 'Google Workspace'…), quando c'è. */
  provider?: string;
  /** Avvertenza da leggere prima di inserire la password. */
  note?: string;
  /** Dove si genera la password per le app. */
  helpUrl?: string;
  /** false = con questo gestore non si può collegare. */
  supported?: boolean;
  /** Il server ha risposto davvero, non è un'ipotesi. */
  verified?: boolean;
}

export interface Folder {
  id: string;
  path: string;
  name: string;
  role: string | null;
  total_count: number;
  unread_count: number;
}

export type FolderRole = 'inbox' | 'sent' | 'drafts' | 'trash' | 'spam' | 'archive';

export interface MessageListItem {
  id: string;
  uid: number;
  subject: string;
  snippet: string;
  from_address: { name: string; email: string } | null;
  to_addresses: { name: string; email: string }[];
  is_read: boolean;
  is_starred: boolean;
  has_attachments: boolean;
  received_at: string | null;
  thread_id: string;
  message_count: number;
  ai_category: string | null;
  ai_urgency: 'low' | 'medium' | 'high' | 'critical' | null;
  snoozed_until: string | null;
  is_archived: boolean;
  thread_starred: boolean;
  labels: string[];
  /** Avviso del filtro spam: la mail sembra spam o una truffa. */
  security_level?: 'warning' | 'danger' | null;
}

/** L'avviso del filtro spam su un messaggio in arrivo. */
export interface AvvisoSicurezza {
  kind: 'danger' | 'warning' | 'rescued';
  verdict: string;
  confidence: number | null;
  reasons: string[];
}

export interface MessageDetail extends MessageListItem {
  body_text: string | null;
  body_html: string | null;
  cc_addresses: { name: string; email: string }[];
  in_reply_to: string | null;
  message_id: string | null;
  folder_path: string;
  account_id: string;
}

export interface ThreadDetail {
  id: string;
  subject: string;
  ai_category: string | null;
  ai_urgency: string | null;
  ai_summary: string | null;
  ai_action_items: string[] | null;
  message_count: number;
  participants: { name?: string; email?: string }[];
  is_starred: boolean;
  is_archived: boolean;
  snoozed_until: string | null;
  labels: string[];
}

export interface AttachmentItem {
  id: string;
  filename: string;
  contentType: string | null;
  sizeBytes: number;
  cached: boolean;
}

export interface Draft {
  id: string;
  account_id: string;
  in_reply_to_message_id: string | null;
  forward_of_message_id: string | null;
  thread_id: string | null;
  subject: string;
  to_addresses: { name?: string; address: string }[];
  cc_addresses: { name?: string; address: string }[];
  bcc_addresses: { name?: string; address: string }[];
  body_text: string;
  body_html: string;
  scheduled_send_at: string | null;
  sent_at: string | null;
  send_error: string | null;
  updated_at: string;
  /** Proposta orari agganciata alla bozza (persistita: sopravvive a riapertura/undo). */
  meeting_json: { token: string; url: string; blockHtml: string; count: number; title: string } | null;
}

export interface DraftAttachment {
  id: string;
  filename: string;
  content_type: string | null;
  size_bytes: number;
}

export interface Reminder {
  id: string;
  thread_id: string;
  message_id: string | null;
  remind_at: string;
  reason: string | null;
  triggered_at: string | null;
  dismissed_at: string | null;
  subject: string;
}

export interface UserSettings {
  signature_html: string;
  signature_text: string;
  default_reply_tone: 'formale' | 'amichevole' | 'breve' | 'dettagliato';
  theme: 'system' | 'light' | 'dark';
  notifications_enabled: boolean;
  keyboard_shortcuts_enabled: boolean;
  auto_save_draft_seconds: number;
  inbox_zero_summary_time: string;
}

// ============= AUTH =============
export const authApi = {
  register: (data: { email: string; password: string; fullName?: string; inviteCode?: string }) =>
    api.post<{ user: User; invite?: { ok: boolean; message?: string } }>('/auth/register', data),
  login: async (data: { email: string; password: string }) => {
    const result = await api.post<{ user: User; tokens: AuthTokens }>('/auth/login', data);
    tokenStore.set(result.tokens.accessToken, result.tokens.refreshToken);
    return result;
  },
  logout: async () => {
    const refresh = tokenStore.getRefresh();
    if (refresh) await api.post('/auth/logout', { refreshToken: refresh }).catch(() => {});
    tokenStore.clear();
  },
  me: () => api.get<{ user: User }>('/auth/me'),
  forgotPassword: (email: string) => api.post<{ ok: true }>('/auth/forgot-password', { email }),
  resetPassword: (token: string, password: string) =>
    api.post<{ ok: true }>('/auth/reset-password', { token, password }),
};

// ============= ACCOUNTS =============
export const accountsApi = {
  list: () => api.get<{ accounts: EmailAccount[] }>('/accounts'),
  discover: (emailAddress: string) =>
    api.post<{ config: DiscoverConfig }>('/accounts/discover', { emailAddress }),
  add: (data: {
    emailAddress: string;
    password: string;
    displayName?: string;
    imapHost?: string;
    imapPort?: number;
    smtpHost?: string;
    smtpPort?: number;
    imapTlsRejectUnauthorized?: boolean;
    smtpTlsRejectUnauthorized?: boolean;
  }) => api.post<{ accountId: string }>('/accounts', data),
  remove: (id: string) => api.delete<{ ok: true }>(`/accounts/${id}`),
  forceSync: (id: string) =>
    api.post<{ ok: true; status: string }>(`/accounts/${id}/sync`),
  /** Server di invio personalizzato (relay) quando l'SMTP del provider rifiuta l'auth. */
  updateSending: (id: string, data: {
    smtpHost: string;
    smtpPort: number;
    smtpSecure?: boolean;
    smtpUsername: string;
    smtpPassword: string;
    smtpTlsRejectUnauthorized?: boolean;
  }) => api.patch<{ ok: true; smtpSecure: boolean; smtpTlsRejectUnauthorized: boolean }>(`/accounts/${id}/sending`, data),
  folders: (id: string) =>
    api.get<{ folders: Folder[] }>(`/accounts/${id}/folders`),
};

// ============= MESSAGES =============
export const messagesApi = {
  list: (params: {
    accountId: string;
    folderRole?: FolderRole;
    folder?: string;
    limit?: number;
    cursor?: string;
    category?: string;
    label?: string;
    urgency?: string;
    unreadOnly?: boolean;
    starredOnly?: boolean;
    hideSnoozed?: boolean;
    hideArchived?: boolean;
  }) => {
    const qs = new URLSearchParams();
    qs.set('accountId', params.accountId);
    if (params.folderRole) qs.set('folderRole', params.folderRole);
    if (params.folder) qs.set('folder', params.folder);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.cursor) qs.set('cursor', params.cursor);
    if (params.category) qs.set('category', params.category);
    if (params.label) qs.set('label', params.label);
    if (params.urgency) qs.set('urgency', params.urgency);
    if (params.unreadOnly) qs.set('unreadOnly', 'true');
    if (params.starredOnly) qs.set('starredOnly', 'true');
    if (params.hideSnoozed === false) qs.set('hideSnoozed', 'false');
    if (params.hideArchived === false) qs.set('hideArchived', 'false');
    return api.get<{ messages: MessageListItem[]; nextCursor: string | null }>(
      `/messages?${qs.toString()}`
    );
  },
  detail: (id: string) => api.get<{ message: MessageDetail; security: AvvisoSicurezza | null }>(`/messages/${id}`),
  thread: (id: string) =>
    api.get<{ thread: ThreadDetail; messages: MessageDetail[] }>(`/threads/${id}`),
  markRead: (id: string, read: boolean) =>
    api.post<{ ok: true }>(`/messages/${id}/read`, { read }),
  star: (id: string, starred: boolean) =>
    api.post<{ ok: true }>(`/messages/${id}/star`, { starred }),
};

// ============= THREADS ACTIONS =============
export const threadsApi = {
  archive: (id: string) => api.post<{ ok: true }>(`/threads/${id}/archive`),
  trash: (id: string) => api.post<{ ok: true }>(`/threads/${id}/trash`),
  move: (id: string, targetFolder: string) =>
    api.post<{ ok: true; moved: number }>(`/threads/${id}/move`, { targetFolder }),
  spam: (id: string, blockSender = true) =>
    api.post<{ ok: true; blocked: boolean; sender: string | null }>(`/threads/${id}/spam`, { blockSender }),
  snooze: (id: string, until: string) =>
    api.post<{ ok: true }>(`/threads/${id}/snooze`, { until }),
  unsnooze: (id: string) => api.post<{ ok: true }>(`/threads/${id}/unsnooze`),
  star: (id: string, starred: boolean) =>
    api.post<{ ok: true }>(`/threads/${id}/star`, { starred }),
  setLabels: (id: string, labels: string[]) =>
    api.post<{ ok: true }>(`/threads/${id}/labels`, { labels }),
  markRead: (id: string, read: boolean) =>
    api.post<{ ok: true }>(`/threads/${id}/read`, { read }),
};

export type SignatureStyle = 'minimal' | 'corporate' | 'creative' | 'tech';
export const signatureApi = {
  generate: (data: {
    full_name: string;
    role?: string;
    company?: string;
    phone?: string;
    mobile?: string;
    email?: string;
    website?: string;
    address?: string;
    linkedin?: string;
    logo_url?: string;
    style: SignatureStyle;
  }) => api.post<{ html: string; plain_text: string }>('/ai/signature/generate', data),
};

// ============= DRAFTS =============
export const draftsApi = {
  list: () => api.get<{ drafts: Draft[] }>('/drafts'),
  detail: (id: string) =>
    api.get<{ draft: Draft; attachments: DraftAttachment[] }>(`/drafts/${id}`),
  create: (data: Partial<Draft> & { accountId: string }) =>
    api.post<{ draftId: string }>('/drafts', data),
  update: (id: string, data: Partial<Draft>) =>
    api.patch<{ ok: true }>(`/drafts/${id}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/drafts/${id}`),
  uploadAttachment: (id: string, data: { filename: string; contentType?: string; base64: string }) =>
    api.post<{ attachmentId: string; sizeBytes: number }>(`/drafts/${id}/attachments`, data),
  removeAttachment: (id: string, attachmentId: string) =>
    api.delete<{ ok: true }>(`/drafts/${id}/attachments/${attachmentId}`),
  send: (id: string, scheduledSendAt?: string) =>
    api.post<{ ok: true; scheduled?: boolean }>(`/drafts/${id}/send`, scheduledSendAt ? { scheduledSendAt } : {}),
};

// ============= ATTACHMENTS =============
export const attachmentsApi = {
  list: (messageId: string) =>
    api.get<{ attachments: AttachmentItem[] }>(`/messages/${messageId}/attachments`),
  downloadUrl: (id: string) => `/api/v1/attachments/${id}/download`,
};

// ============= REMINDERS =============
export const remindersApi = {
  list: () => api.get<{ reminders: Reminder[] }>('/reminders'),
  create: (data: { threadId: string; messageId?: string; remindAt: string; reason?: string }) =>
    api.post<{ reminderId: string }>('/reminders', data),
  dismiss: (id: string) => api.post<{ ok: true }>(`/reminders/${id}/dismiss`),
  remove: (id: string) => api.delete<{ ok: true }>(`/reminders/${id}`),
};

// ============= SETTINGS =============
export const settingsApi = {
  get: () => api.get<{ settings: UserSettings }>('/settings'),
  update: (data: Partial<UserSettings>) =>
    api.patch<{ ok: true }>('/settings', {
      signatureHtml: data.signature_html,
      signatureText: data.signature_text,
      defaultReplyTone: data.default_reply_tone,
      theme: data.theme,
      notificationsEnabled: data.notifications_enabled,
      keyboardShortcutsEnabled: data.keyboard_shortcuts_enabled,
      autoSaveDraftSeconds: data.auto_save_draft_seconds,
      inboxZeroSummaryTime: data.inbox_zero_summary_time,
    }),
};

// ============= AI =============
export const aiApi = {
  smartReply: (data: {
    threadId: string;
    tone?: 'formale' | 'amichevole' | 'breve' | 'dettagliato';
    instructions?: string;
  }) => api.post<{ draftId: string; body: string }>('/ai/smart-reply', data),
  summarizeThread: (threadId: string) =>
    api.post<{ summary: string }>(`/ai/summarize-thread/${threadId}`),
  search: (data: { query: string; accountId?: string; limit?: number; synthesize?: boolean }) =>
    api.post<{ results: MessageListItem[]; synthesis: string | null }>('/ai/search', data),
  compose: (data: { intent: string; tone?: 'formale' | 'amichevole' | 'breve' | 'dettagliato'; to?: string }) =>
    api.post<{ subject: string; body: string }>('/ai/compose', data),
  contactSummary: (email: string) =>
    api.post<{ summary: string; count: number }>('/ai/contact-summary', { email }),
};

export interface FollowUp {
  thread_id: string;
  message_id: string;
  account_id: string;
  subject: string;
  to: string;
  to_name: string | null;
  last_sent_at: string;
  days_waiting: number;
}
export const followUpsApi = {
  list: (days = 3) => api.get<{ followUps: FollowUp[] }>(`/follow-ups?days=${days}`),
};

// ============= SEARCH =============
// Due strade: le parole rispondono subito (si cerca mentre si scrive),
// la risposta dell'AI arriva a parte perché è l'unica lenta.
export const searchApi = {
  keyword: (q: string, accountId?: string, limit = 50) => {
    const qs = new URLSearchParams({ q, limit: String(limit) });
    if (accountId) qs.set('accountId', accountId);
    return api.get<{ messages: MessageListItem[] }>(`/search?${qs.toString()}`);
  },
  answer: (data: { query: string; accountId?: string; limit?: number }) =>
    api.post<{ answer: string | null; sources: MessageListItem[] }>('/search/answer', data),
};

// ============= CONTACTS (Rubrica) =============
export interface Contact {
  id: string;
  account_id: string;
  email: string;
  name: string | null;
  company: string | null;
  role: string | null;
  phone: string | null;
  notes: string | null;
  is_favorite: boolean;
  tags: string[];
  source: string;
  interaction_count: number;
  last_interaction_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContactInput {
  email: string;
  name?: string | null;
  company?: string | null;
  role?: string | null;
  phone?: string | null;
  notes?: string | null;
  tags?: string[];
  is_favorite?: boolean;
}

// ============= LABELS (Etichette) =============
export interface Label { id: string; name: string; color: string; created_at: string }
export const labelsApi = {
  list: () => api.get<{ labels: Label[] }>('/labels'),
  create: (name: string, color?: string) => api.post<{ label: Label }>('/labels', { name, color }),
  update: (id: string, data: { name?: string; color?: string }) => api.patch<{ label: Label }>(`/labels/${id}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/labels/${id}`),
};

// ============= CALENDARIO =============
export interface CalendarEvent {
  id: string;
  title: string;
  description: string | null;
  location: string | null;
  start_at: string;
  end_at: string;
  all_day: boolean;
  color: string;
  source: string;
  source_message_id: string | null;
}
export interface CalendarEventInput {
  title: string;
  description?: string;
  location?: string;
  start_at: string;
  end_at: string;
  all_day?: boolean;
  color?: string;
}
export const calendarApi = {
  range: (from: string, to: string) =>
    api.get<{ events: CalendarEvent[] }>(`/calendar/events?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
  create: (data: CalendarEventInput) => api.post<{ event: CalendarEvent }>('/calendar/events', data),
  update: (id: string, data: Partial<CalendarEventInput>) => api.patch<{ event: CalendarEvent }>(`/calendar/events/${id}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/calendar/events/${id}`),
  // Google Calendar (sync bidirezionale)
  googleStatus: () => api.get<{ connected: boolean }>('/ai/calendar/google/status'),
  googleAuthUrl: () => api.get<{ auth_url: string }>('/ai/calendar/google/auth-url'),
  googleDisconnect: () => api.post<{ ok: true }>('/ai/calendar/google/disconnect'),
  googleSync: () => api.post<{ connected: boolean; pushed: number; pulled: number; deleted: number }>('/calendar/google/sync'),
  // Scadenzario AI: suggerimenti di eventi estratti dalle email
  pending: () => api.get<{ events: CalendarSuggestion[] }>('/ai/calendar/pending'),
  scan: () => api.post<{ scanned: number; found: number }>('/ai/calendar/scan', {}),
  confirmSuggestion: (eventId: string) => api.post<{ ok: true; eventId: string }>('/ai/calendar/confirm', { event_id: eventId }),
  dismissSuggestion: (eventId: string) => api.post<{ ok: true }>('/ai/calendar/dismiss', { event_id: eventId }),
};

export interface ExtractedDocument {
  id: string;
  message_id: string;
  thread_id: string | null;
  doc_type: string;
  doc_number: string | null;
  amount: string | null;
  currency: string | null;
  doc_date: string | null;
  due_date: string | null;
  counterpart: string | null;
  subject: string | null;
  created_at: string;
}
export const documentsApi = {
  list: (type?: string) => api.get<{ documents: ExtractedDocument[] }>(`/documents${type ? `?type=${encodeURIComponent(type)}` : ''}`),
  scan: () => api.post<{ scanned: number; found: number }>('/documents/scan', {}),
  remove: (id: string) => api.delete<{ ok: true }>(`/documents/${id}`),
};

export interface CalendarSuggestion {
  id: string;
  message_id: string;
  title: string;
  start_iso: string;
  end_iso: string;
  location: string | null;
  description: string | null;
  confidence: number;
}

// ============= RISPOSTE AI (proattive) =============
export interface AiReplySuggestion {
  id: string;
  reply_text: string;
  created_at: string;
  account_id: string;
  thread_id: string;
  message_id: string;
  subject: string | null;
  from_address: { name?: string; email?: string } | null;
  snippet: string | null;
  received_at: string | null;
  account_email: string;
}
export const aiRepliesApi = {
  list: () => api.get<{ suggestions: AiReplySuggestion[] }>('/ai/replies'),
  generate: () => api.post<{ created: number }>('/ai/replies/generate', {}),
  update: (id: string, reply_text: string) => api.patch<{ ok: true }>(`/ai/replies/${id}`, { reply_text }),
  dismiss: (id: string) => api.post<{ ok: true }>(`/ai/replies/${id}/dismiss`, {}),
  send: (id: string, reply_text?: string) =>
    api.post<{ ok: true }>(`/ai/replies/${id}/send`, reply_text ? { reply_text } : {}),
};

// ============= INBOX ZERO =============
export type InboxZeroCategory = 'to_reply' | 'to_archive' | 'to_delete' | 'newsletter' | 'promo';
export type InboxZeroAction = 'archive' | 'delete' | 'mark_read';
export interface InboxZeroCategorized {
  message_id: string;
  category: InboxZeroCategory;
  reason: string;
  confidence: number;
  subject: string;
  from: string;
  received_at: string;
}
export interface InboxZeroRun {
  id: string;
  account_id: string;
  started_at: string | null;
  completed_at: string | null;
  messages_total: number;
  messages_archived: number;
  messages_deleted: number;
  messages_skipped: number;
}
export const inboxZeroApi = {
  preview: (accountId: string, limit = 20) =>
    api.get<{ messages: unknown[]; categorized: InboxZeroCategorized[] }>(
      `/ai/inbox-zero/preview?account_id=${accountId}&limit=${limit}`
    ),
  apply: (accountId: string, actions: { message_id: string; action: InboxZeroAction }[]) =>
    api.post<{ ok: true; archived: number; deleted: number; marked: number }>(
      '/ai/inbox-zero/apply',
      { account_id: accountId, actions }
    ),
  history: () => api.get<{ runs: InboxZeroRun[] }>('/ai/inbox-zero/history'),
};

// ============= RULES (Filtri/Regole) =============
export type RuleField = 'from' | 'to' | 'subject';
export type RuleOp = 'contains' | 'not_contains' | 'equals' | 'starts_with' | 'ends_with';
export interface RuleCondition { field: RuleField; op: RuleOp; value: string }
export interface RuleConditions { match: 'all' | 'any'; conditions: RuleCondition[] }
export type RuleActionType = 'mark_read' | 'star' | 'set_category' | 'archive' | 'trash';
export interface RuleAction { type: RuleActionType; value?: string }
export interface Rule {
  id: string;
  account_id: string | null;
  name: string;
  is_active: boolean;
  priority: number;
  conditions: RuleConditions;
  actions: RuleAction[];
  created_at: string;
  updated_at: string;
}
export interface RuleInput {
  name: string;
  account_id?: string | null;
  is_active?: boolean;
  priority?: number;
  conditions: RuleConditions;
  actions: RuleAction[];
}
export const rulesApi = {
  list: () => api.get<{ rules: Rule[] }>('/rules'),
  create: (data: RuleInput) => api.post<{ rule: Rule }>('/rules', data),
  update: (id: string, data: Partial<RuleInput>) => api.patch<{ rule: Rule }>(`/rules/${id}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/rules/${id}`),
};

// ============= AUTORESPONDER (Out-of-office) =============
export interface Autoresponder {
  account_id: string;
  enabled: boolean;
  subject: string;
  body_text: string;
  body_html: string | null;
  start_at: string | null;
  end_at: string | null;
}

export const autoresponderApi = {
  get: (accountId: string) =>
    api.get<{ autoresponder: Autoresponder }>(`/accounts/${accountId}/autoresponder`),
  update: (accountId: string, data: Partial<Omit<Autoresponder, 'account_id'>>) =>
    api.put<{ autoresponder: Autoresponder }>(`/accounts/${accountId}/autoresponder`, data),
  generate: (data: { fullName?: string; company?: string; fromDate?: string; toDate?: string; reason?: string; altContact?: string }) =>
    api.post<{ subject: string; body_text: string }>(`/ai/autoresponder/generate`, data),
};

export const contactsApi = {
  list: (params: { search?: string; favoritesOnly?: boolean; accountId?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.favoritesOnly) qs.set('favoritesOnly', 'true');
    if (params.accountId) qs.set('accountId', params.accountId);
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.offset) qs.set('offset', String(params.offset));
    return api.get<{ contacts: Contact[]; total: number }>(`/contacts?${qs.toString()}`);
  },
  suggest: (q: string) =>
    api.get<{ contacts: Contact[] }>(`/contacts/suggest?q=${encodeURIComponent(q)}`),
  get: (id: string) => api.get<{ contact: Contact }>(`/contacts/${id}`),
  create: (accountId: string, data: ContactInput) =>
    api.post<{ contact: Contact }>('/contacts', { accountId, ...data }),
  update: (id: string, data: Partial<ContactInput>) =>
    api.patch<{ contact: Contact }>(`/contacts/${id}`, data),
  remove: (id: string) => api.delete<{ ok: true }>(`/contacts/${id}`),
  import: (accountId: string) =>
    api.post<{ ok: true; imported: number }>('/contacts/import', { accountId }),
  enrich: (id: string) => api.post<{ contact: Contact }>(`/contacts/${id}/enrich`),
};

// --- Proponi orari (meeting proposals) ---

export interface MeetingSlot { start: string; end: string }

export interface PublicMeetingProposal {
  token: string;
  title: string;
  duration_minutes: number;
  timezone: string;
  slots: MeetingSlot[];
  status: string;
  booked_slot: MeetingSlot | null;
  organizer_name: string;
  organizer_email: string | null;
}

export const meetingsApi = {
  suggest: (data: { durationMinutes?: number; days?: number; count?: number } = {}) =>
    api.post<{ slots: MeetingSlot[] }>('/meetings/suggest', data),
  create: (data: { accountId?: string; title: string; durationMinutes: number; slots: MeetingSlot[]; withVideo?: boolean }) =>
    api.post<{ token: string; url: string; blockHtml: string; slots: MeetingSlot[] }>('/meetings/proposals', data),
  getPublic: (token: string) =>
    api.get<{ proposal: PublicMeetingProposal }>(`/public/meet/${token}`),
  book: (token: string, data: { slotIndex: number; name?: string; email?: string }) =>
    api.post<{ ok: true; slot: MeetingSlot; title: string; timezone: string; organizer_name: string; confirmationEmailSent: boolean }>(`/public/meet/${token}/book`, data),
};

// --- La Reception (agente che risponde da solo) ---

export interface ReceptionSettings {
  id: string;
  account_id: string;
  enabled: boolean;
  mode: 'auto' | 'approval';
  business_name: string | null;
  business_url: string | null;
  allowed_intents: Record<string, boolean>;
  escalation_rules: string | null;
  min_confidence: number;
  max_replies_per_day: number;
  payment_link: string | null;
  payment_policy: string | null;
  last_crawl_at: string | null;
}

export interface ReceptionLogItem {
  id: string;
  message_id: string;
  thread_id: string | null;
  intent: string | null;
  confidence: number | null;
  action: 'sent' | 'pending' | 'approved' | 'rejected' | 'escalated' | 'skipped';
  reason: string | null;
  from_email: string | null;
  orig_subject: string | null;
  reply_subject: string | null;
  reply_body: string | null;
  created_at: string;
  resolved_at: string | null;
}

export interface ReceptionStats { today_sent: number; month_sent: number; off_hours: number; pending: number }

export interface ReceptionKbItem { id: string; source: 'site' | 'manual'; url: string | null; title: string | null; preview: string; updated_at: string }

export const receptionApi = {
  settings: (accountId: string) =>
    api.get<{ settings: ReceptionSettings | null; kbChunks: number }>(`/reception/settings?accountId=${accountId}`),
  saveSettings: (data: { accountId: string } & Partial<{
    enabled: boolean; mode: 'auto' | 'approval'; businessName: string | null; businessUrl: string | null;
    allowedIntents: Record<string, boolean>; escalationRules: string | null; minConfidence: number;
    maxRepliesPerDay: number; paymentLink: string | null; paymentPolicy: string | null;
  }>) => api.put<{ settings: ReceptionSettings }>('/reception/settings', data),
  crawl: (accountId: string, url: string) =>
    api.post<{ ok: true; pages: number; chunks: number }>('/reception/crawl', { accountId, url }),
  kb: (accountId: string) => api.get<{ items: ReceptionKbItem[] }>(`/reception/kb?accountId=${accountId}`),
  addKb: (accountId: string, title: string, content: string) =>
    api.post<{ item: ReceptionKbItem }>('/reception/kb', { accountId, title, content }),
  removeKb: (id: string) => api.delete<{ ok: true }>(`/reception/kb/${id}`),
  log: (accountId: string, limit = 40) =>
    api.get<{ items: ReceptionLogItem[]; stats: ReceptionStats }>(`/reception/log?accountId=${accountId}&limit=${limit}`),
  approve: (id: string, body?: string) =>
    api.post<{ ok: true }>(`/reception/log/${id}/approve`, body ? { body } : {}),
  reject: (id: string, note?: string) =>
    api.post<{ ok: true }>(`/reception/log/${id}/reject`, note ? { note } : {}),
};

// --- Invio Blagomail (rete di consegna) ---

export interface DnsRecordToSet { label: string; type: string; host: string; value: string; ok: boolean }

export interface SendingDomain {
  id: string;
  domain: string;
  brevo_verified: boolean;
  brevo_authenticated: boolean;
  dns_records: DnsRecordToSet[];
  enabled: boolean;
  last_error: string | null;
  last_checked_at: string | null;
}

export interface DeliveryMessage {
  id: string;
  message_id: string;
  account_id: string | null;
  to_email: string | null;
  subject: string | null;
  via: 'own' | 'network';
  status: 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'spam' | 'deferred';
  sent_at: string;
  delivered_at: string | null;
  first_opened_at: string | null;
  open_count: number;
  click_count: number;
  bounce_reason: string | null;
}

export interface DeliveryStats { sent: number; delivered: number; opened: number; bounced: number; open_rate: number; score: number }

export const deliveryApi = {
  domains: () => api.get<{ domains: SendingDomain[]; candidates: string[]; available: boolean }>('/delivery/domains'),
  activate: (accountId: string) => api.post<{ domain: SendingDomain }>('/delivery/domains', { accountId }),
  check: (domain: string) => api.post<{ domain: SendingDomain }>(`/delivery/domains/${domain}/check`),
  toggle: (domain: string, enabled: boolean) => api.patch<{ domain: SendingDomain }>(`/delivery/domains/${domain}`, { enabled }),
  audit: (domain: string) => api.get<{ audit: Record<string, unknown> }>(`/delivery/domains/${domain}/audit`),
  messages: (limit = 50) => api.get<{ messages: DeliveryMessage[]; stats: DeliveryStats }>(`/delivery/messages?limit=${limit}`),
};

// --- Pannello staff Blagoweb ---

export interface AdminClientRow {
  user_id: string; email: string; full_name: string | null; created_at: string;
  account_id: string | null; email_address: string | null;
  domain: string | null; brevo_verified: boolean | null; brevo_authenticated: boolean | null;
  enabled: boolean | null; dns_records: DnsRecordToSet[] | null; last_error: string | null;
  sent_30d: number; bounced_30d: number; opened_30d: number;
}

export interface InviteCode {
  id: string;
  code: string;
  plan: string;
  max_uses: number;
  uses: number;
  months: number | null;
  expires_at: string | null;
  note: string | null;
  created_at: string;
  redeemed: { email: string; usedAt: string }[];
}

export const adminApi = {
  inviteCodes: () => api.get<{ codes: InviteCode[] }>('/admin/invite-codes'),
  createInviteCode: (body: {
    plan: string; maxUses?: number; months?: number | null;
    expiresInDays?: number | null; note?: string;
  }) => api.post<{ code: InviteCode }>('/admin/invite-codes', body),
  deleteInviteCode: (id: string) => api.delete<{ ok: true }>(`/admin/invite-codes/${id}`),
  overview: () => api.get<{
    clients: AdminClientRow[];
    totals: { sent: number; bounced: number; domains: number; active: number };
    network: { smtpConfigured: boolean; apiConfigured: boolean; webhookConfigured: boolean; webhookUrl: string | null };
  }>('/admin/overview'),
  bulkActivate: (accountIds: string[]) =>
    api.post<{ results: { account: string; domain: string; ok: boolean; message: string; records?: DnsRecordToSet[] }[] }>(
      '/admin/delivery/bulk-activate', { accountIds }),
  recheck: () => api.post<{ checked: number; authenticated: number }>('/admin/delivery/recheck'),
  spamOverview: () => api.get<{
    modes: Record<ModoSpam, number>;
    activity: { filtered7d: number; rescued7d: number; toReview: number; warnings7d: number; aiCalls7d: number };
  }>('/admin/spam/overview'),
  spamSetMode: (mode: ModoSpam, only?: ModoSpam) => api.put<{ updated: number }>('/admin/spam/mode', { mode, only }),
  toggle: (domain: string, enabled: boolean) => api.patch<{ domain: { domain: string; enabled: boolean } }>(`/admin/delivery/${domain}`, { enabled }),
};

// --- Videochiamate white-label ---

export interface PublicRoomInfo {
  token: string;
  title: string;
  starts_at: string | null;
  ends_at: string | null;
  ended: boolean;
  organizer_name: string;
  logo_url: string | null;
  brand_color: string;
  jitsi_domain: string;
  room_name: string;
}

// --- Abbonamento ---

export interface Plan {
  id: 'solo' | 'essenziale' | 'pro' | 'business';
  label: string; price: number; priceYearly: number;
  mailboxes: number; receptionReplies: number; receptionAuto: boolean;
  transcriptionMinutes: number; storageGb: number;
  delivery: boolean; sharedMailboxes: boolean;
}

export interface Usage { used: number; limit: number }

export interface MyBilling {
  plan: Plan;
  planId: Plan['id'];
  status: string;
  trialing: boolean;
  inGrace: boolean;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  usage: {
    mailboxes: Usage;
    receptionReplies: Usage;
    transcriptionMinutes: Usage;
    storage: { usedBytes: number; limitBytes: number };
  };
}

export interface DowngradeImpact {
  mailboxesOver: number;
  storageOver: number;
  losesReceptionAuto: boolean;
  losesDelivery: boolean;
  losesSharedMailboxes: boolean;
}

export const billingApi = {
  plans: () => api.get<{ plans: Plan[]; extraMailboxPrice: number; trialDays: number; paymentsEnabled: boolean }>('/billing/plans'),
  checkout: (plan: string, yearly = false) => api.post<{ url: string }>('/billing/checkout', { plan, yearly }),
  portal: () => api.post<{ url: string }>('/billing/portal'),
  me: () => api.get<MyBilling>('/billing/me'),
  impact: (plan: string) => api.get<DowngradeImpact>(`/billing/impact?plan=${plan}`),
  changePlan: (plan: string) => api.post<{ applied: boolean; effectiveAt: string | null }>('/billing/change-plan', { plan }),
  cancelChange: () => api.delete<{ ok: true }>('/billing/change-plan'),
  redeem: (code: string) => api.post<{ ok: true }>('/billing/redeem', { code }),
  resendVerification: () => api.post<{ ok: true }>('/auth/resend-verification'),
  verifyEmail: (token: string) => api.post<{ email: string }>('/auth/verify-email', { token }),
};

// --- Domini del cliente ---

export interface OwnedDomain {
  id: string;
  domain: string;
  token: string;
  verified: boolean;
  hostedByUs: boolean | null;
  txtRecord: { name: string; value: string };
  lastError: string | null;
  checkedAt: string | null;
}

export const domainsApi = {
  list: () => api.get<{ domains: OwnedDomain[]; mailboxCreation: boolean; txtName: string }>('/domains'),
  add: (domain: string) => api.post<{ domain: OwnedDomain }>('/domains', { domain }),
  verify: (id: string) => api.post<{ domain: OwnedDomain }>(`/domains/${id}/verify`),
  remove: (id: string) => api.delete<{ ok: true }>(`/domains/${id}`),
  createMailbox: (id: string, local: string, password: string) =>
    api.post<{ email: string }>(`/domains/${id}/mailboxes`, { local, password }),
};

// --- Link di trasferimento (file pesanti) ---

export interface FileShare {
  id: string; token: string; filename: string; content_type: string | null;
  size_bytes: string; expires_at: string; downloads: number;
  max_downloads: number | null; last_download_at: string | null;
  revoked_at: string | null; expired: boolean; created_at: string; url: string;
}

export interface PublicShare {
  filename: string; sizeBytes: number; contentType: string | null;
  needsPassword: boolean; expiresAt: string; unavailable: string | null;
  brand: { name: string | null; logoUrl: string | null; color: string | null };
}

export const filesApi = {
  list: () => api.get<{ items: FileShare[] }>('/files'),
  revoke: (id: string) => api.delete<{ ok: true }>(`/files/${id}`),
  publicInfo: (token: string) => api.get<PublicShare>(`/public/files/${token}`),
  // upload e download passano da fetch diretta: multipart e binario
};

// --- Archivio allegati ---

export interface ArchiveItem {
  id: string;
  filename: string;
  content_type: string | null;
  size_bytes: number | null;
  kind: 'immagine' | 'pdf' | 'foglio' | 'documento' | 'archivio' | 'altro';
  message_id: string;
  thread_id: string | null;
  subject: string | null;
  from_address: { name?: string; email?: string } | null;
  received_at: string | null;
  doc_type: string | null;
  doc_number: string | null;
  amount: string | null;
  currency: string | null;
  doc_date: string | null;
  due_date: string | null;
  counterpart: string | null;
  similarity?: number;
}

export interface ArchiveStats {
  total: number;
  bytes: number;
  kinds: { kind: ArchiveItem['kind']; n: number; bytes: string }[];
  contacts: { name: string | null; email: string; n: number }[];
}

export const archiveApi = {
  list: (params: { q?: string; contact?: string; kind?: string; from?: string; to?: string; limit?: number; offset?: number } = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => [k, String(v)])
    ).toString();
    return api.get<{ items: ArchiveItem[]; hasMore: boolean }>(`/archive${qs ? `?${qs}` : ''}`);
  },
  search: (q: string) => api.post<{ items: ArchiveItem[]; query: string }>('/archive/search', { query: q }),
  stats: () => api.get<ArchiveStats>('/archive/stats'),
  // il download passa da attachmentsApi.downloadUrl: è lo stesso endpoint
};

export interface VideoRoomItem {
  id: string; token: string; title: string;
  starts_at: string | null; ends_at: string | null; ended_at: string | null;
  guest_name: string | null; guest_email: string | null;
  summary: string | null;
  has_minutes: boolean; minutes_pending: boolean; url: string;
}

export interface MeetingMinutesDetail extends VideoRoomItem {
  decisions: string[] | null;
  action_items: { what: string; who: string | null }[] | null;
  next_steps: string | null;
  minutes_sent_at: string | null;
  has_recording: boolean;
  has_transcript: boolean;
  transcript: string | null;
}

export const videoApi = {
  status: () => api.get<{ available: boolean; domain: string }>('/video/status'),
  create: (data: { title: string; accountId?: string; eventId?: string; startsAt?: string; endsAt?: string; guestEmail?: string; guestEmails?: string[]; guestName?: string }) =>
    api.post<{ room: { id: string; token: string }; url: string; invited: number; recipients: number }>('/video/rooms', data),
  list: () => api.get<{ rooms: VideoRoomItem[] }>('/video/rooms'),
  detail: (id: string, withTranscript = false) =>
    api.get<{ room: MeetingMinutesDetail; participants: { name: string | null; email: string | null; joined_at: string }[] }>(
      `/video/rooms/${id}${withTranscript ? '?transcript=1' : ''}`
    ),
  end: (id: string) => api.post<{ ok: true }>(`/video/rooms/${id}/end`),
  // pubbliche (nessun login richiesto)
  publicRoom: (token: string) => api.get<{ room: PublicRoomInfo }>(`/public/call/${token}`),
  join: (token: string, name: string, email?: string) =>
    api.post<{ jwt: string; roomName: string; domain: string }>(`/public/call/${token}/join`, { name, email }),
};

// --- Identità di marca (logo cliente sulle pagine pubbliche) ---

export interface BrandSettings {
  id: string; account_id: string;
  business_name: string | null; logo_url: string | null;
  brand_color: string; custom_domain: string | null;
}

export const brandApi = {
  get: (accountId: string) => api.get<{ brand: BrandSettings | null; suggestedLogo: string | null }>(`/brand?accountId=${accountId}`),
  save: (data: { accountId: string; businessName?: string | null; logoUrl?: string | null; brandColor?: string; customDomain?: string | null }) =>
    api.put<{ brand: BrandSettings }>('/brand', data),
};


// ============= FILTRO SPAM =============
export type ModoSpam = 'off' | 'suggest' | 'prudent' | 'auto';
export type VerdettoSpam = 'legit' | 'spam' | 'phishing' | 'uncertain';

export interface VoceSpam {
  /** quarantine = nella cartella spam del server; message = segnato spam da te in Blagomail. */
  kind: 'quarantine' | 'message';
  id: string;
  subject: string | null;
  snippet: string | null;
  from_address: { name?: string; email?: string } | null;
  received_at: string | null;
  has_attachments: boolean;
  is_read: boolean;
  verdict: VerdettoSpam | null;
  confidence: number | null;
  reasons: string[];
  action: 'suggested' | 'rescued' | 'filtered' | null;
  decided_by: 'rules' | 'ai' | 'user' | null;
}

export interface DettaglioSpam extends VoceSpam {
  body_text: string;
  attachments: { filename: string; contentType: string | null; size: number }[];
}

export interface VoceLista {
  id: string;
  kind: 'allow' | 'block';
  match: 'email' | 'domain';
  value: string;
  source: 'user' | 'feedback';
  created_at: string;
}

export type FiltroSpam = 'review' | 'all' | 'filtered' | 'rescued';

export const spamApi = {
  summary: (accountId: string) =>
    api.get<{ mode: ModoSpam; inSpam: number; daControllare: number; filtrate7g: number; rimesse7g: number; avvisiInArrivo: number }>(
      `/spam/summary?accountId=${accountId}`),
  items: (accountId: string, filter: FiltroSpam, cursor?: string) =>
    api.get<{ items: VoceSpam[]; nextCursor: string | null }>(
      `/spam/items?accountId=${accountId}&filter=${filter}${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`),
  detail: (id: string, kind: VoceSpam['kind']) =>
    api.get<{ item: DettaglioSpam }>(`/spam/items/${id}?kind=${kind}`),
  notSpam: (id: string, kind: VoceSpam['kind'], allow: 'email' | 'domain' | 'none' = 'email') =>
    api.post<{ ok: true }>(`/spam/items/${id}/not-spam`, { kind, allow }),
  confirmSpam: (id: string, kind: VoceSpam['kind'], block = true) =>
    api.post<{ ok: true }>(`/spam/items/${id}/confirm-spam`, { kind, block }),
  lists: (accountId: string) =>
    api.get<{ allow: VoceLista[]; block: VoceLista[] }>(`/spam/lists?accountId=${accountId}`),
  addToList: (data: { accountId: string; kind: 'allow' | 'block'; match: 'email' | 'domain'; value: string }) =>
    api.post<{ allow: VoceLista[]; block: VoceLista[] }>('/spam/lists', data),
  removeFromList: (id: string) => api.delete<{ ok: true }>(`/spam/lists/${id}`),
  setMode: (accountId: string, mode: ModoSpam) =>
    api.put<{ mode: ModoSpam }>('/spam/settings', { accountId, mode }),
  dismissWarning: (messageId: string) =>
    api.post<{ ok: true }>(`/messages/${messageId}/security/dismiss`),
};
